import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApiNa} from '@di-shared/api/di';
import {ERROR} from '@di-shared/const';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useAvailableComponentNa from '@di-shared/features/home/na/useAvailableComponent.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useQueryClient} from '@tanstack/react-query';

export interface BenefitContentsNaProps {
  vin: string;
}

function BenefitContentsNa({vin}: BenefitContentsNaProps) {
  const {t} = useInferableTranslation();

  return (
    <section
      id="tabpanel03"
      className="section"
      aria-labelledby="tab03"
    >
      <section className="section section_ubi">
        <ErrorBoundary
          fallback={
            <div className="section_container">
              <div className="section_container_header">
                <div className="container_header_title">
                  <h3 className="label_medium_b">{t('ctn_ubi_LN_component_car_insurance_benefits_title')}</h3>
                  <strong className="title_xxsmall">{ERROR.DASH}</strong>
                </div>
                <div className="container_header_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
                  />
                </div>
              </div>
              <p className="section_container_desc">{ERROR.DASH}</p>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="section_container">
                <div className="section_container_header">
                  <div className="container_header_title">
                    <h3 className="label_medium_b skeleton">{t('ctn_ubi_LN_component_car_insurance_benefits_title')}</h3>
                    <strong className="title_xxsmall skeleton">{t('ctn_ubi_LN_component_car_insurance_benefits_title')}</strong>
                  </div>
                  <div className="container_header_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt=""
                      kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn_large btn_full skeleton"
                >
                  {t('ctn_ubi_LN_component_learn_more_button')}
                </button>
              </div>
            }
          >
            <Inner vin={vin} />
          </Suspense>
        </ErrorBoundary>
      </section>
    </section>
  );
}

function Inner({vin}: BenefitContentsNaProps) {
  const {t} = useInferableTranslation();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();
  const {isUbiAgreed} = useAvailableComponentNa({vin});

  async function openAgree() {
    const confirmed = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE.getFullPath(), {
      vin,
    });
    if (!confirmed) {
      return;
    }
    try {
      await diApiNa.vehicle.consentSelectiveTermsCode({vin, termscode: 'UBI', agree: true});
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApiNa.vehicle.getVehicleInfo), vin]});
    } catch (error) {
      await resolveError(error);
    }
  }

  async function openWithdraw() {
    const confirmed = await ccWebviewUtils.openBottomSheetWebViewUrl(
      ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW.getFullPath(),
      {vin}
    );
    if (!confirmed) {
      return;
    }
    try {
      await diApiNa.vehicle.consentSelectiveTermsCode({vin, termscode: 'UBI', agree: false});
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApiNa.vehicle.getVehicleInfo), vin]});
    } catch (error) {
      await resolveError(error);
    }
  }

  // UBI 동의 상태
  // NA는 EU와 달리 보험사 오퍼 조회(offer) 개념이 없음 — 항상 고정 문구만 표시 (Figma 스펙 확인됨)
  if (isUbiAgreed) {
    return (
      <>
        <div className="section_container">
          <div className="section_container_header">
            <div className="container_header_title">
              <h3 className="label_medium_b">{t('ctn_ubi_LN_component_car_insurance_benefits_title')}</h3>
              <strong className="title_xxsmall">{t('msg_ubi_LN_component_participating_program_title')}</strong>
            </div>
            <div className="container_header_imgbox">
              <ImageSelector
                className="driving_banner_img"
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
              />
            </div>
          </div>
          <p className="section_container_desc">{t('msg_ubi_LN_component_participating_program_contents')}</p>
        </div>
        <button
          type="button"
          className="consent_withdraw_btn"
          onClick={openWithdraw}
        >
          {t('ctn_ubi_LN_component_leave_program_button')}
        </button>
      </>
    );
  }

  // UBI 미동의 상태
  return (
    <div className="section_container">
      <div className="section_container_header">
        <div className="container_header_title">
          <h3 className="label_medium_b">{t('ctn_ubi_LN_component_car_insurance_benefits_title')}</h3>
          <strong className="title_xxsmall">{t('ctn_ubi_LN_component_congratulations_title')}</strong>
        </div>
        <div className="container_header_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt=""
            kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
          />
        </div>
      </div>

      <p className="section_container_desc">{t('msg_ubi_LN_component_eligible_for_ubi_contents')}</p>
      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={openAgree}
      >
        {t('ctn_ubi_LN_component_learn_more_button')}
      </button>
    </div>
  );
}

export default BenefitContentsNa;
